import React, { FC, ReactNode } from "react";
import { useReservation } from "./reservationContext";
import { useLocale } from "../i18n/locale";
import { formatStayDate } from "../i18n/formatDate";

export interface StaySummaryProps {
  className?: string;
}

const DetailRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className="mt-[15px] flex items-center justify-between gap-4 border-t border-line pt-[15px] text-[13px]">
    <span className="text-[#93989c]">{label}</span>
    <strong className="font-bold text-navy">{children}</strong>
  </div>
);

/**
 * The right column. It does not change between steps by design — it is the
 * tenant's anchor: which stay all this paperwork is for.
 */
const StaySummary: FC<StaySummaryProps> = ({ className = "" }) => {
  const { locale, t } = useLocale();
  const reservation = useReservation();
  const stay = t.desktop.stay;

  return (
    <aside
      className={`border-l border-[#ebe7df] bg-cream px-[38px] pb-[30px] pt-[58px] ${className}`}
    >
      <p className="text-[12px] font-extrabold uppercase tracking-[0.19em] text-gold min-[760px]:text-[12px]">
        {stay.eyebrow}
      </p>

      <div className="mt-[18px] bg-white shadow-[0_9px_30px_#1b263017]">
        <div className="relative h-[230px] overflow-hidden bg-[#d8d0c3]">
          <img
            src={reservation.photo}
            alt={reservation.property}
            className="h-full w-full object-cover object-[center_54%]"
          />
          {/* Darkens the lower half so the badge stays legible on any photo. */}
          <span
            className="absolute inset-0 bg-gradient-to-b from-transparent from-[52%] to-[#0f203561]"
            aria-hidden="true"
          />
          <span className="absolute bottom-[11px] left-[14px] z-[2] bg-[#13263feb] px-2.5 py-2 text-[11px] min-[760px]:text-[8px] uppercase tracking-[0.15em] text-white">
            {reservation.propertyBadge}
          </span>
        </div>

        <div className="p-[26px]">
          <small className="text-[12px] font-extrabold uppercase tracking-[0.15em] text-gold min-[760px]:text-[12px]">
            {reservation.district}
          </small>
          <h3 className="mb-[22px] mt-[7px] font-serif text-[23px] font-normal text-navy min-[760px]:text-[27px]">
            {reservation.property}
          </h3>

          <dl className="mb-5 grid grid-cols-2 gap-[14px]">
            <div>
              <dt className="text-[12px] uppercase tracking-[0.1em] text-[#9b9fa2] min-[760px]:text-[12px]">
                {stay.checkIn}
              </dt>
              <dd className="text-[13px] font-bold text-navy min-[760px]:text-[15px]">
                {formatStayDate(reservation.checkIn, locale)}
              </dd>
            </div>
            <div className="border-l border-line pl-[18px]">
              <dt className="text-[12px] uppercase tracking-[0.1em] text-[#9b9fa2] min-[760px]:text-[12px]">
                {stay.checkOut}
              </dt>
              <dd className="text-[13px] font-bold text-navy min-[760px]:text-[15px]">
                {formatStayDate(reservation.checkOut, locale)}
              </dd>
            </div>
          </dl>

          <DetailRow label={stay.tenantName}>
            {reservation.tenantName}
          </DetailRow>
          <DetailRow label={stay.bookingReference}>
            {reservation.bookingReference}
          </DetailRow>

          <div className="mt-[15px] flex items-center justify-between gap-4 border-t border-line pt-[15px]">
            <span className="text-[13px] text-[#93989c]">{stay.bookedVia}</span>
            {/* The SVG marks vary from square (Vrbo, Europea) to very wide
                (Immoweb, Homes & Villas), so the width is fixed and the height
                follows: object-contain then letterboxes rather than stretching
                whichever shape a channel happens to use. */}
            <img
              src={reservation.channelLogo}
              alt={reservation.channel}
              className="h-auto w-[112px] object-contain"
            />
          </div>
        </div>
      </div>

      <p className="mt-7 text-center text-[11px] leading-[1.6] text-[#aaa69e] min-[760px]:text-[10px]">
        {stay.fineprint}
        <br />
        {/* TODO: the year is the render year; swap for the reservation's own
            once the DMS supplies it. */}
        {stay.copyright(new Date().getFullYear())}
      </p>
    </aside>
  );
};

export default StaySummary;
